import type { JSX } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Account, type Category, type Transaction } from "../api.js";
import { useNumberFormat } from "../contexts/numberFormat/index.js";
import { Modal } from "../components/Modal.js";
import { Field, Select, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { cn } from "../lib/utils.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table.js";
import { majorToMinor, minorToMajorInput } from "../money.js";

export function Transactions(): JSX.Element {
  const qc = useQueryClient();
  const { formatMoney } = useNumberFormat();
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const txs = useQuery({
    queryKey: ["txs", q],
    queryFn: () => api.transactions({ q: q || undefined, limit: 100 }),
  });
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: () => api.list<Account>("/accounts") });
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api.list<Category>("/categories") });
  const remove = useMutation({
    mutationFn: (id: string) => api.remove(`/transactions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["txs"] }),
  });

  const acctName = (id: string) => accounts.data?.items.find((a) => a.id === id)?.name ?? "?";

  return (
    <>
      <PageHeader title="Transactions">
        <Input
          type="search"
          placeholder="search description / counterparty"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
          }}
          className="w-full sm:w-64"
        />
        <Button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          + Add
        </Button>
      </PageHeader>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Counterparty</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {txs.data?.items.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{new Date(t.occurred_at).toLocaleDateString()}</TableCell>
                <TableCell>{acctName(t.account_id)}</TableCell>
                <TableCell>{t.counterparty ?? "—"}</TableCell>
                <TableCell>{t.description ?? "—"}</TableCell>
                <TableCell
                  className={cn(
                    "text-right font-medium",
                    t.direction === "debit" ? "text-bad" : "text-good",
                  )}
                >
                  {t.direction === "debit" ? "-" : "+"}
                  {formatMoney(t.amount_minor, t.currency, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditing(t);
                        setShowForm(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm("Delete?")) remove.mutate(t.id);
                      }}
                    >
                      Del
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {txs.data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground py-8 text-center">
                  No transactions.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false);
        }}
        title={editing ? "Edit transaction" : "New transaction"}
      >
        <TransactionForm
          initial={editing}
          accounts={accounts.data?.items ?? []}
          categories={cats.data?.items ?? []}
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ["txs"] });
          }}
        />
      </Modal>
    </>
  );
}

function TransactionForm({
  initial,
  accounts,
  categories,
  onDone,
}: {
  initial: Transaction | null;
  accounts: Account[];
  categories: Category[];
  onDone: () => void;
}): JSX.Element {
  const [accountId, setAccountId] = useState(initial?.account_id ?? accounts[0]?.id ?? "");
  const [categoryId, setCategoryId] = useState(initial?.category_id ?? "");
  const [direction, setDirection] = useState<"debit" | "credit">(initial?.direction ?? "debit");
  const [amountMajor, setAmountMajor] = useState(
    initial ? minorToMajorInput(initial.amount_minor) : "0",
  );
  const [currency, setCurrency] = useState(initial?.currency ?? accounts[0]?.currency ?? "EUR");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [counterparty, setCounterparty] = useState(initial?.counterparty ?? "");
  const [occurredAt, setOccurredAt] = useState(initial?.occurred_at ?? new Date().toISOString());
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        account_id: accountId,
        category_id: categoryId || null,
        direction,
        amount_minor: majorToMinor(amountMajor),
        currency: currency.toUpperCase(),
        description: description || null,
        counterparty: counterparty || null,
        occurred_at: occurredAt,
      };
      return initial
        ? api.update(`/transactions/${initial.id}`, body)
        : api.create("/transactions", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Account *">
        <Select
          required
          value={accountId}
          onChange={(e) => {
            setAccountId(e.target.value);
          }}
        >
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.currency})
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Category">
        <Select
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
          }}
        >
          <option value="">— none —</option>
          {categories
            .filter((c) => !c.is_archived)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.type} / {c.name}
              </option>
            ))}
        </Select>
      </Field>
      <Field label="Type">
        <Select
          value={direction}
          onChange={(e) => {
            setDirection(e.target.value as "debit" | "credit");
          }}
        >
          <option value="debit">Spend (money out)</option>
          <option value="credit">Income (money in)</option>
        </Select>
      </Field>
      <Field label="Amount *">
        <TextInput
          required
          type="number"
          step="0.01"
          value={amountMajor}
          onChange={(e) => {
            setAmountMajor(e.target.value);
          }}
        />
      </Field>
      <Field label="Currency *">
        <TextInput
          required
          maxLength={3}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value.toUpperCase());
          }}
        />
      </Field>
      <Field label="Description">
        <TextInput
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />
      </Field>
      <Field label="Counterparty">
        <TextInput
          value={counterparty}
          onChange={(e) => {
            setCounterparty(e.target.value);
          }}
        />
      </Field>
      <Field label="Date / time *">
        <TextInput
          required
          type="datetime-local"
          value={occurredAt.slice(0, 16)}
          onChange={(e) => {
            setOccurredAt(new Date(e.target.value).toISOString());
          }}
        />
      </Field>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving..." : "Save"}
      </Button>
    </form>
  );
}
